// Create / edit a Lead Generator roster row. Gated LeadGen.Manage at the call
// site. A Lead Generator is an external contractor who sources preleads — not a
// broker and not a Phoenix staff user; provisioning their portal login is a
// separate "Send portal invite" action.

import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button, FormField, Input, SubmitButton, Textarea, useToast } from "@/design-system/components/ui";
import { extractErrorMessage } from "@/api/client";
import { calendarDateInputToIso } from "@/design-system/lib/format";
import { useDateFormatter } from "@/utils/datetime";
import { useCreateLeadGenerator, useLeadGenerator, useUpdateLeadGenerator } from "@/hooks/leadGen/useLeadGenerators";

export function GeneratorFormModal({
  generatorId,
  onClose,
}: {
  /** Set = edit that generator; omitted/null = create a new one. */
  generatorId?: string | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const { isoDate } = useDateFormatter();
  const isEdit = !!generatorId;
  const detail = useLeadGenerator(generatorId ?? null);
  const create = useCreateLeadGenerator();
  const update = useUpdateLeadGenerator();

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [hiredDate, setHiredDate] = useState("");
  const [notes, setNotes] = useState("");
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (isEdit && detail.data && !seeded) {
      setDisplayName(detail.data.displayName);
      setEmail(detail.data.email);
      setPhone(detail.data.phone ?? "");
      setHiredDate(detail.data.hiredAtUtc ? isoDate(detail.data.hiredAtUtc) : "");
      setNotes(detail.data.notes ?? "");
      setSeeded(true);
    }
  }, [isEdit, detail.data, seeded, isoDate]);

  const pending = create.isPending || update.isPending;
  const invalid = displayName.trim() === "" || email.trim() === "";

  const save = () => {
    if (invalid) return;
    const body = {
      displayName: displayName.trim(),
      email: email.trim(),
      phone: phone.trim() || null,
      hiredAtUtc: hiredDate ? calendarDateInputToIso(hiredDate) : null,
      notes: notes.trim() || null,
    };
    const onSuccess = () => {
      toast.success(isEdit ? "Generator updated" : "Generator created");
      onClose();
    };
    const onError = (err: unknown) => toast.error(extractErrorMessage(err));
    if (isEdit && generatorId) {
      update.mutate({ id: generatorId, body }, { onSuccess, onError });
    } else {
      create.mutate(body, { onSuccess, onError });
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={isEdit ? "Edit lead generator" : "New lead generator"}
      description="Roster identity only. Provisioning their portal login is the separate “Send portal invite” action."
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} type="button">
            Cancel
          </Button>
          <SubmitButton
            type="button"
            size="sm"
            pending={pending}
            pendingLabel="Saving…"
            disabled={invalid}
            onClick={save}
          >
            {isEdit ? "Save changes" : "Create generator"}
          </SubmitButton>
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        <FormField label="Display name" required htmlFor="gen-name">
          <Input
            id="gen-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            invalid={displayName.trim() === ""}
            placeholder="e.g. Jordan Rivera"
          />
        </FormField>
        <FormField label="Email" required htmlFor="gen-email" helperText="Used for the portal invitation.">
          <Input
            id="gen-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            invalid={email.trim() === ""}
            placeholder="name@example.com"
          />
        </FormField>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-space-4">
          <FormField label="Phone" htmlFor="gen-phone">
            <Input id="gen-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </FormField>
          <FormField label="Hired date" htmlFor="gen-hired">
            <Input id="gen-hired" type="date" value={hiredDate} onChange={(e) => setHiredDate(e.target.value)} />
          </FormField>
        </div>
        <FormField label="Notes" htmlFor="gen-notes">
          <Textarea id="gen-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        </FormField>
      </div>
    </Modal>
  );
}
